import React from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup, { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';

import { CENTRAL_FILL_MODE } from 'enums/workQueue.enum';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1,
    borderLeft: '1px solid transparent',
  },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  textTransform: 'none',
  border: '1px solid #ccc',
  backgroundColor: '#f5f5f5',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: '#e0e0e0',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

/**
 * POM-6099 — switches the Central Fill Rx tab between its two views.
 *
 * Rendering is the CALLER's decision, not this component's: the caller knows whether the user may
 * view relayed orders, and it must gate the relay CONTENT on that role as well, because the mode
 * outlives a sign-out -- `resetAppState` does not reset the workQueue slice.
 */
const CentralFillQueueModeSwitch = ({ mode, onChange }) => (
  <Paper
    elevation={0}
    sx={(theme) => ({
      display: 'flex',
      border: `1px solid ${theme.palette.divider}`,
      flexWrap: 'wrap',
    })}
  >
    <StyledToggleButtonGroup
      size='small'
      value={mode}
      exclusive
      aria-label='queue mode'
      // `exclusive` hands back null when the selected button is clicked again. Ignoring that keeps
      // a mode always selected rather than leaving the tab in no mode at all.
      onChange={(_event, next) => next && onChange(next)}
    >
      <StyledToggleButton value={CENTRAL_FILL_MODE.CENTRAL_FILL} aria-label='central fill orders'>
        Central Fill
      </StyledToggleButton>
      <StyledToggleButton value={CENTRAL_FILL_MODE.RELAY} aria-label='relayed orders'>
        Relayed Orders
      </StyledToggleButton>
    </StyledToggleButtonGroup>
  </Paper>
);

CentralFillQueueModeSwitch.propTypes = {
  mode: PropTypes.oneOf(Object.values(CENTRAL_FILL_MODE)).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default CentralFillQueueModeSwitch;
